/** Catalog snapshot status for staging Mini App — MINIAPP-075 / FUNCTIONALITY-081. */

export interface CatalogSnapshotStatus {
  sourceLabel: string;
  lastUpdated: string | null;
  stale: boolean;
  demoMarked: boolean;
  stockWarning: string | null;
  checkoutHint: string;
  snapshotId: string | null;
  readOnly: boolean;
  cartEnabled: boolean;
  checkoutEnabled: boolean;
  stagingPromCommerce: boolean;
  categoryCount: number | null;
  productCount: number | null;
}

export function readCatalogSnapshotStatus(meta: unknown): CatalogSnapshotStatus | null {
  if (!meta || typeof meta !== "object") return null;
  const m = meta as Record<string, unknown>;
  if (m.ok !== true) return null;

  const demoMarked = m.demo_marked === true || m.source === "demo_fixture";
  const stale = m.stale === true;
  const warnings = Array.isArray(m.warnings) ? m.warnings.map(String) : [];
  const commerce = (m.commerce ?? {}) as Record<string, unknown>;
  const cartEnabled = commerce.cart_enabled === true;
  const checkoutEnabled = commerce.checkout_enabled === true;
  const stagingPromCommerce = commerce.staging_prom_commerce === true;

  const stockWarning =
    !demoMarked && warnings.some((w) => String(w).startsWith("stock_status_unknown"))
      ? "Some products have unknown stock — blocked from cart."
      : demoMarked
        ? "Demo snapshot — not real catalog."
        : null;

  let sourceLabel: string;
  if (demoMarked) {
    sourceLabel = "DEMO fixture (not real catalog)";
  } else if (m.source === "prom_catalog_snapshot") {
    sourceLabel = "Prom catalog snapshot (R2 staging)";
  } else if (m.fallback_source === "r2_catalog_snapshot") {
    sourceLabel = `R2 snapshot (${String(m.source ?? "unknown")})`;
  } else if (m.mode === "live") {
    sourceLabel = "live";
  } else {
    sourceLabel = m.source ? String(m.source) : "unknown";
  }

  let checkoutHint: string;
  if (demoMarked) {
    checkoutHint = "Demo catalog — browse only; cart/checkout blocked.";
  } else if (stale) {
    checkoutHint = "Snapshot stale — cart/checkout blocked until refreshed.";
  } else if (stagingPromCommerce && cartEnabled) {
    checkoutHint =
      "Staging Prom commerce open for sellable products (price+stock). Payment/NP still off.";
  } else if (m.degraded === true || m.read_only === true) {
    checkoutHint =
      "R2 snapshot is read-only for commerce; cart/checkout blocked for this source.";
  } else {
    checkoutHint = "Checkout allowed only for sellable products (price+stock).";
  }

  return {
    sourceLabel,
    lastUpdated: typeof m.generated_at === "string" ? m.generated_at : null,
    stale,
    demoMarked,
    stockWarning,
    checkoutHint,
    snapshotId: typeof m.snapshot_id === "string" ? m.snapshot_id : null,
    readOnly: m.read_only === true && !cartEnabled,
    cartEnabled,
    checkoutEnabled,
    stagingPromCommerce,
    categoryCount: typeof m.category_count === "number" ? m.category_count : null,
    productCount: typeof m.product_count === "number" ? m.product_count : null,
  };
}

export function isClientLineSellable(input: {
  priceResolved?: boolean;
  unitPriceUah?: number | null;
  stockStatus?: string | null;
}): { ok: boolean; reason: string } {
  if (input.priceResolved !== true || input.unitPriceUah == null || Number(input.unitPriceUah) <= 0) {
    return { ok: false, reason: "unknown_price" };
  }
  const stock = String(input.stockStatus ?? "unknown").toLowerCase();
  if (stock === "out_of_stock") return { ok: false, reason: "out_of_stock" };
  if (stock !== "in_stock" && stock !== "low_stock") return { ok: false, reason: "stock_unknown" };
  return { ok: true, reason: "ok" };
}
